interface HeroScreenshotProps {
  src: string;
  alt: string;
  className?: string;
}

/**
 * Derives WebP and 2x versions from a base image path.
 * e.g., "/screenshots/dashboard.png" -> { webp: "/screenshots/dashboard.webp", png2x: "/screenshots/dashboard@2x.png", webp2x: "/screenshots/dashboard@2x.webp" }
 */
function getImageVariants(src: string) {
  const extension = src.substring(src.lastIndexOf('.'));
  const basePath = src.substring(0, src.lastIndexOf('.'));

  return {
    png: src,
    png2x: `${basePath}@2x${extension}`,
    webp: `${basePath}.webp`,
    webp2x: `${basePath}@2x.webp`,
  };
}

/**
 * Flat screenshot image for hero section (Mintlify style).
 * - Full image, no crop/zoom
 * - Subtle shadow for depth
 * - White fade mask at bottom
 * - Lazy loading for performance
 * - WebP with PNG fallback
 * - Retina (2x) support via srcset
 */
export function HeroScreenshot({ src, alt, className = '' }: HeroScreenshotProps) {
  const variants = getImageVariants(src);

  return (
    <div className={`relative ${className}`}>
      <picture>
        {/* WebP sources with 1x and 2x for retina */}
        <source type="image/webp" srcSet={`${variants.webp} 1x, ${variants.webp2x} 2x`} />
        {/* PNG fallback with 1x and 2x for retina */}
        <source type="image/png" srcSet={`${variants.png} 1x, ${variants.png2x} 2x`} />
        {/* Fallback img element */}
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          className="w-full h-auto rounded-xl border border-border-primary"
        />
      </picture>
      {/* White fade mask at bottom - blends into white background */}
      <div
        className="absolute bottom-0 left-0 right-0 h-32 md:h-48 xl:h-72 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, transparent 0%, white 85%)',
        }}
      />
    </div>
  );
}
